import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  IconButton,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import { useAuthContext } from "@/context/AuthContext";
import { useVerifyAddress } from "@/api/verifyAddress";
// Move states outside to avoid recreation on each render
const states = [
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
];

export const UserCard = ({
  productPresent,
  onMissingInfoUpdate,
  editMode,
  setEditMode,
}: any) => {
  const { user } = useAuthContext();
  const addressInfo = user.addressInfo || {};

  const [userDetails, setUserDetails]: any = useState({
    address: addressInfo.address || "",
    zip: addressInfo.zip || "",
    city: addressInfo.city || "",
    state: addressInfo.state || "",
    phoneNumber: addressInfo.phoneNumber || "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [uspsCorrection, setUspsCorrection]: any = useState(null);
  const [verifyAddress] = useVerifyAddress();

  // Helper Functions
  const isValidPhoneNumber = (phone: string) => /^\d{10}$/.test(phone);

  // Format phone number by removing non-digit characters and enforcing 10 digits.
  const formatPhoneNumber = (phone: string) =>
    phone.replace(/\D/g, "").slice(0, 10);

  const capitalizeDetails = (details: any) =>
    Object.fromEntries(
      Object.entries(details).map(([key, value]: any) => [
        key,
        value.toUpperCase(),
      ]),
    );

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    // Clear USPS correction when user modifies the form
    if (uspsCorrection) {
      setUspsCorrection(null);
    }

    if (name === "zip") {
      const zip = value.replace(/\D/g, "").slice(0, 5);
      setUserDetails((prevDetails: any) => ({ ...prevDetails, zip }));
    } else if (name === "phoneNumber") {
      const phoneNumber = formatPhoneNumber(value); // Normalize the phone number
      setUserDetails((prevDetails: any) => ({ ...prevDetails, phoneNumber }));
    } else {
      setUserDetails((prevDetails: any) => ({ ...prevDetails, [name]: value }));
    }
  };

  const handleSave = async () => {
    setLoading(true);
    setError("");
    setUspsCorrection(null);

    const { address, city, state, zip, phoneNumber } = userDetails;

    if (!isValidPhoneNumber(phoneNumber)) {
      setError("Please enter a valid 10-digit phone number.");
      setLoading(false);
      return;
    }

    try {
      const result = await verifyAddress({
        address,
        city,
        state,
        zipCode: zip,
        phoneNumber,
      });

      if (!result.valid) {
        throw new Error(result.error || "Address verification failed");
      }

      if (result.verificationData?.address) {
        const {
          ZIPCode: validZIPCode,
          city: validCity,
          state: validState,
          streetAddress: validStreetAddress,
        } = result.verificationData.address;

        if (
          validZIPCode !== zip ||
          validCity !== city ||
          validState !== state ||
          validStreetAddress !== address
        ) {
          setUspsCorrection({
            address: validStreetAddress,
            city: validCity,
            state: validState,
            zip: validZIPCode,
          });
          return;
        }
      }

      setEditMode(false);
    } catch (e: any) {
      setError(`Error updating user details: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptUSPSCorrection = async () => {
    if (!uspsCorrection) return;
    const correctedDetails = {
      ...capitalizeDetails(uspsCorrection),
      phoneNumber: userDetails.phoneNumber,
    };
    setUserDetails(correctedDetails);
    setUspsCorrection(null);

    // Redo verification with corrected address
    setLoading(true);
    setError("");

    try {
      const result = await verifyAddress({
        address: correctedDetails.address,
        city: correctedDetails.city,
        state: correctedDetails.state,
        zipCode: correctedDetails.zip,
        phoneNumber: correctedDetails.phoneNumber,
      });

      if (!result.valid) {
        throw new Error(result.error || "Address verification failed");
      }

      setEditMode(false);
    } catch (e: any) {
      setError(`Error updating user details: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const missingInfo = ["address", "city", "state", "zip", "phoneNumber"].filter(
    (field: any) => !userDetails[field] || userDetails[field].trim() === "",
  );

  useEffect(() => {
    onMissingInfoUpdate(missingInfo);
  }, [productPresent, userDetails]);

  return (
    <Box sx={{ maxWidth: 450 }} data-cypress="checkout-user-card">
      <Typography
        variant="subtitle1"
        sx={{ fontWeight: "bold", mb: 1 }}
        data-cypress="delivery-info-title"
      >
        Delivery Info
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 1 }}
        data-cypress="user-name"
      >
        Name: {user.firstName} {user.lastName}
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 1 }}
        data-cypress="user-email"
      >
        Email: {user.email}
      </Typography>

      {productPresent && (
        <>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography variant="subtitle2" fontWeight={600}>
              Delivery Address:
            </Typography>
            {!editMode && (
              <IconButton
                onClick={() => setEditMode(true)}
                sx={{
                  color: "text.secondary",
                  ml: 1,
                  "&:hover": { color: "primary.main" },
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            )}
          </Box>

          {!editMode ? (
            <Box>
              {userDetails.address &&
              userDetails.city &&
              userDetails.state &&
              userDetails.zip &&
              isValidPhoneNumber(userDetails.phoneNumber) ? (
                <>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    {userDetails.address}
                  </Typography>
                  {userDetails.city && userDetails.state && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      {userDetails.city}, {userDetails.state} {userDetails.zip}
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    Phone Number: {userDetails.phoneNumber}
                  </Typography>
                </>
              ) : (
                <Typography variant="body2" color="red" sx={{ mb: 1 }}>
                  Address and phone number required to purchase products.
                </Typography>
              )}
            </Box>
          ) : (
            <Box>
              <TextField
                name="address"
                label="Address"
                value={userDetails.address}
                onChange={handleChange}
                fullWidth
                margin="normal"
                autoComplete="street-address"
                sx={{ mb: 2 }}
                helperText={userDetails.address === "" && "Required"}
                error={userDetails.address === ""}
              />

              <TextField
                name="city"
                label="City"
                value={userDetails.city}
                onChange={handleChange}
                fullWidth
                margin="normal"
                autoComplete="address-level2"
                sx={{ mb: 2 }}
                helperText={userDetails.city === "" && "Required"}
                error={userDetails.city === ""}
              />

              <FormControl fullWidth margin="normal" sx={{ mb: 2 }}>
                <InputLabel>State</InputLabel>
                <Select
                  name="state"
                  value={userDetails.state}
                  onChange={handleChange}
                  label="State"
                  autoComplete="address-level1"
                  MenuProps={{
                    PaperProps: {
                      style: { maxHeight: 400 },
                    },
                  }}
                >
                  {states.map((state) => (
                    <MenuItem key={state} value={state}>
                      {state}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                name="zip"
                label="Zip Code"
                value={userDetails.zip}
                onChange={handleChange}
                fullWidth
                margin="normal"
                autoComplete="postal-code"
                sx={{ mb: 2 }}
                inputProps={{ maxLength: 5 }}
                helperText={userDetails.zip === "" && "Required"}
                error={userDetails.zip === ""}
              />

              <TextField
                name="phoneNumber"
                label="Phone Number"
                value={userDetails.phoneNumber}
                onChange={handleChange}
                fullWidth
                margin="normal"
                autoComplete="tel"
                sx={{ mb: 2 }}
                inputProps={{ maxLength: 14 }}
                helperText={
                  !isValidPhoneNumber(userDetails.phoneNumber) &&
                  "Please enter a valid 10-digit phone number."
                }
                error={!isValidPhoneNumber(userDetails.phoneNumber)}
              />

              <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                disabled={loading || missingInfo.length > 0 || uspsCorrection}
                startIcon={
                  loading ? <CircularProgress size={20} /> : <SaveIcon />
                }
                fullWidth
                sx={{ mt: 1, mb: 2 }}
              >
                {loading ? "Saving..." : "Save"}
              </Button>

              {error && (
                <Typography color="error" sx={{ mt: 2, textAlign: "center" }}>
                  {error}
                </Typography>
              )}
            </Box>
          )}
          {uspsCorrection && (
            <Box sx={{ mt: 2 }}>
              <Typography color="text.secondary">
                {`We weren't able to find your address, did you mean this:`}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {uspsCorrection.address}, {uspsCorrection.city},{" "}
                {uspsCorrection.state} {uspsCorrection.zip}
              </Typography>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleAcceptUSPSCorrection}
                disabled={loading}
                sx={{
                  mt: 2,
                  opacity: loading ? 0.6 : 1,
                  cursor: loading ? "not-allowed" : "pointer",
                }}
              >
                {loading ? "Processing..." : "Accept Correction"}
              </Button>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default UserCard;
